import includes from "lodash/includes";
import without from "lodash/without";
import union from "lodash/union";
import difference from "lodash/difference";
import flatMap from "lodash/flatMap";

// A top-level taxonomy and the sub-taxonomies displayed under it are always enabled and
// disabled together, as showing a top-level taxonomy means showing every element it
// groups on the map.
export const branchTaxonomyIdsFor = mapTaxonomy => [
  mapTaxonomy.id,
  ...mapTaxonomy.children.map(({ id }) => id)
];

// The sub-taxonomies of a filter's taxonomies are displayed inside its dropdowns, so
// they're selectable within the filter and belong to its own scope.
export const selectableTaxonomyIdsFor = mapTaxonomies =>
  flatMap(mapTaxonomies, branchTaxonomyIdsFor);

// A dropdown being open is what keeps the taxonomies it contains enabled, so a filter
// that starts by showing its elements must start with its dropdowns open, otherwise its
// top-level switches would read as disabled while their elements are displayed.
export const initiallyOpenDropdownIdsFor = (
  mapTaxonomies,
  filterElementsOutOnInitialize
) => {
  if (filterElementsOutOnInitialize) return [];

  return mapTaxonomies.filter(({ hasChildren }) => hasChildren).map(({ id }) => id);
};

const toggledTaxonomyIds = (activeTaxonomyIds, mapTaxonomyId) => {
  if (includes(activeTaxonomyIds, mapTaxonomyId)) {
    return without(activeTaxonomyIds, mapTaxonomyId);
  }

  return [...activeTaxonomyIds, mapTaxonomyId];
};

// The taxonomies a filter shows, and the dropdowns it displays open, once the visitor has
// clicked one of its selectors. Both are computed as a whole so that the filter component
// only has to apply them, and so that the interactions between the filter's options can
// be read in a single place.
//
export const taxonomySelectionAfterClick = ({
  mapTaxonomies,
  mapTaxonomyId,
  activeTaxonomyIds,
  openDropdowns,
  allowSelectingMultipleTaxonomies
}) => {
  const topLevelTaxonomy = mapTaxonomies.find(({ id }) => id === mapTaxonomyId);

  // Sub-taxonomies stay freely selectable within the top-level taxonomy they're displayed
  // under, as the single selection option doesn't apply to them.
  if (!topLevelTaxonomy) {
    return {
      activeTaxonomyIds: toggledTaxonomyIds(activeTaxonomyIds, mapTaxonomyId),
      openDropdowns
    };
  }

  const branchTaxonomyIds = branchTaxonomyIdsFor(topLevelTaxonomy);

  // Disabling a top-level taxonomy hides every element it groups, and collapses the
  // dropdown its sub-taxonomies are displayed in.
  if (includes(activeTaxonomyIds, topLevelTaxonomy.id)) {
    return {
      activeTaxonomyIds: difference(activeTaxonomyIds, branchTaxonomyIds),
      openDropdowns: without(openDropdowns, topLevelTaxonomy.id)
    };
  }

  const openedDropdownIds = topLevelTaxonomy.hasChildren ? [topLevelTaxonomy.id] : [];

  // The single selection option applies to the filter's top-level taxonomies : enabling
  // one of them hides the elements grouped under all the others.
  if (!allowSelectingMultipleTaxonomies) {
    return {
      activeTaxonomyIds: branchTaxonomyIds,
      openDropdowns: openedDropdownIds
    };
  }

  return {
    activeTaxonomyIds: union(activeTaxonomyIds, branchTaxonomyIds),
    openDropdowns: union(openDropdowns, openedDropdownIds)
  };
};
